import { useToast } from '@shared/hooks/use-toast';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeletePack } from '../../hooks/useDeletePack';
import { Pack } from '../../models/Pack';

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithPack: (pack: Pack) => void;
}

const DeletePackModal = forwardRef<ExtendedModalCenterRef, {}>((props, ref) => {
  // 🎯 Hook de traducciones
  const { t } = useTranslation('packsModule');
  const { toast } = useToast();
  const deletePackMutation = useDeletePack();

  // 🎯 Referencia interna al ModalCenter para evitar recursión
  const internalModalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado interno del modal - NO causa re-render del padre
  const [pack, setPack] = useState<Pack | null>(null);

  const resetForm = () => {
    setPack(null);
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleDelete = async () => {
    try {
      if (!pack) {
        toast({
          title: t('errors.generic'),
          description: t('modal.delete.error_no_selection'),
          variant: 'destructive',
        });
        return;
      }

      // 🎯 Solo llamar al hook - él maneja los toasts
      await deletePackMutation.deletePack(pack.id);

      // 🎯 Solo cerrar modal si fue exitoso
      resetForm();
      handleCancel();
    } catch (error: any) {
      // 🎯 El hook ya maneja el toast de error
      console.error('Error deleting pack:', error);
    }
  };

  // 🎯 ÚNICO useCallback necesario - Se pasa a useImperativeHandle
  const openWithPack = useCallback((packData: Pack) => {
    setPack(packData);
    internalModalRef.current?.openModal();
  }, []);

  // 🎯 Exponer métodos personalizados usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithPack, // ← Método nuevo para uso directo
    }),
    [openWithPack],
  );

  const isLoading = deletePackMutation.isDeleting;

  return (
    <ModalCenter ref={internalModalRef} title={t('modal.delete.title')} type="error">
      <div className="space-y-4">
        <p className="text-sm text-text">
          {t('modal.delete.confirmation')} <strong>{pack?.title}</strong>?
        </p>
        <p className="text-sm text-text">{t('modal.delete.confirmation_subtitle')}</p>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="lg"
            onClick={handleCancel}
            name="cancel"
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            {t('button.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            name="delete-btn-confirm"
            onClick={handleDelete}
            className="w-full sm:w-auto"
            disabled={isLoading}
            data-testid={`delete-pack-button-${pack?.id}`}
          >
            {isLoading ? t('button.deleting') : t('button.delete')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

export default DeletePackModal;
DeletePackModal.displayName = 'DeletePackModal';
